import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonScope = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Python Scope & The LEGB Rule</h1>
                <p className="subtitle">Understanding how Python finds, modifies, and hides variables.</p>
            </div>

            <div className="content-section">
                <h2>Introduction: Where does a variable live?</h2>
                <p>
                    In many programming languages (like C++ or Java), variables are strictly scoped to the block {"{"}{"}"} they are defined in.
                    Python is different. It uses a hierarchy of four scopes to determine what a name means.
                </p>
                <ProTip><p><strong>Crucial Concept:</strong> Scope in Python is determined <strong>at compile time</strong> (Lexical Scoping). When Python compiles your function to bytecode, it decides <em>then and there</em> if a variable is local or global.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>1. The LEGB Hierarchy</h2>

                <h3>L: Local Scope</h3>
                <p>
                    This is the innermost scope. It contains names defined <strong>inside the current function</strong>.
                    This scope is created when the function is called and destroyed when it returns.
                </p>
                <CodeBlock language="python" code={`def my_func():
    x = 10 # Local variable
    print(x)

my_func()
# print(x) #  NameError: name 'x' is not defined`} />

                <h3>E: Enclosing Scope</h3>
                <p>
                    This applies to <strong>nested functions</strong>. If the inner function asks for `x`, and it's not local, Python looks at the outer (enclosing) function.
                </p>
                <CodeBlock language="python" code={`def outer():
    x = "I am outside"
    
    def inner():
        # Python looks in Local (no x) -&gt; Enclosing (found x!)
        print(x) 
    
    inner()

outer() # Output: "I am outside"`} />

                <h3>G: Global Scope</h3>
                <p>
                    This is the top-level scope of the script/module. Variables defined here are visible everywhere (unless shadowed).
                </p>
                <CodeBlock language="python" code={`x = "I am global"

def show_global():
    print(x)

show_global() # Output: "I am global"`} />

                <h3>B: Built-in Scope</h3>
                <p>
                    This is the special system scope that contains keywords like `len`, `print`, `range`, and `str`.
                    It is the last place Python looks.
                </p>
                <CodeBlock language="python" code={`# We never defined 'len', but Python finds it in Built-ins
print(len([1, 2, 3])) # Output: 3`} />
            </div>

            <div className="content-section">
                <h2>2. The "UnboundLocalError" Mystery</h2>
                <ProTip><p>
                        <strong>The Trap:</strong> This is the most common scope error beginners face.
                        "I can read the global variable, but why does my code crash when I try to change it?"
                    </p></ProTip>
                <CodeBlock language="python" code={`x = 10

def crash_me():
    print(x) # We expect this to print 10...
    x = x + 1 # But because we assign to 'x' here...
    
    #  UnboundLocalError: local variable 'x' referenced before assignment`} />

                <h3>Why does this happen?</h3>
                <p>
                    Remember: <strong>Scope is decided at compile time.</strong>
                    When Python sees `x = ...` anywhere inside the function, it tags `x` as a <strong>Local Variable</strong> for the <em>entire</em> function.
                </p>
                <p>
                    So line 1 (`print(x)`) tries to print the <em>local</em> `x`, but the local `x` hasn't been assigned a value yet!
                    It does <strong>not</strong> fall back to the global `x` because the compiler has already decided "`x` is local here".
                </p>
            </div>

            <div className="content-section">
                <h2>2. Modifying Scope: global and nonlocal</h2>
                <p>
                    Reading variables from outer scopes is easy. <strong>Writing</strong> to them requires specific keywords.
                    If you try to assign a value to a global variable inside a function without permission, Python will just create a new local variable instead.
                </p>

                <h3>The `global` Keyword</h3>
                <CodeBlock language="python" code={`score = 0

def update_score():
    # We want to modify the global 'score'
    global score
    score += 10 # This works now

update_score()
print(score) # 10`} />

                <h3>The `nonlocal` Keyword</h3>
                <p>
                    Used in nested functions (Closures) to modify a variable in the <strong>Enclosing</strong> scope.
                </p>
                <CodeBlock language="python" code={`def counter_factory():
    count = 0 # Enclosing variable
    
    def increment():
        nonlocal count
        count += 1
        return count
    
    return increment

my_counter = counter_factory()
print(my_counter()) # 1
print(my_counter()) # 2`} />
            </div>

            <div className="content-section">
                <h2>3. The Danger of Shadowing</h2>
                <ProTip><p>
                        <strong>Shadowing</strong> occurs when you create a variable with the same name as a variable in an outer scope.
                        The most dangerous version is shadowing <strong>Built-in</strong> functions.
                    </p></ProTip>
                <CodeBlock language="python" code={`#  BAD PRACTICE
list = [1, 2, 3] # You just obliterated the 'list()' constructor!

# Later in the code...
my_data = (10, 20)
# clean_data = list(my_data)
#  TypeError: 'list' object is not callable

# You have to restart your kernel to fix this!`} />
                <p>
                    <strong>Common victims:</strong> `list`, `dict`, `str`, `sum`, `id`, `min`, `max`.
                    Always use distinct names like `my_list` or `total_sum`.
                </p>
            </div>

            <div className="content-section">
                <h2>4. The Loop Scope "gotcha"</h2>
                <p>
                    In languages like C++ or Java (and modern JS with `let`), variables declared inside a loop block {"{"}{"}"} stay inside that block.
                    <strong>In Python, loops do NOT create a new scope.</strong>
                </p>

                <CodeBlock language="python" code={`for i in range(5):
    pass

print(i) # Output: 4 (Wait, what?)

# The variable 'i' LEAKS out into the surrounding function!
# This is a feature, not a bug, but it catches beginners off guard.`} />
            </div>

            <div className="content-section">
                <h2>5. List Comprehensions: The Exception</h2>
                <p>
                    In Python 2, list comprehensions leaked variables just like for loops.
                    In Python 3, they were fixed. They now have their own private scope.
                </p>

                <CodeBlock language="python" code={`x = 100

# Using a variable 'x' inside the comprehension
squares = [x*x for x in range(5)]

print(x) 
# Output: 100 (Unchanged!)
# The 'x' inside the loop was temporary and isolated.`} />
            </div>

            <div className="content-section">
                <h2>6. Mutability in Scope</h2>
                <p>
                    A confusing concept is how scope handles mutable objects (lists) vs immutable ones (integers).
                </p>
                <CodeBlock language="python" code={`def confusing(my_list, my_int):
    # Assignment (=) changes the LOCAL reference
    my_int = 999 
    
    # Method calls (.) modify the OBJECT itself (Heap)
    my_list.append(999)

numbers = [1, 2]
n = 10

confusing(numbers, n)

print(n)       # 10 (Protected)
print(numbers) # [1, 2, 999] (Modified!)`} />
            </div>

            <div className="content-section">
                <h2>7. Performance: Inside the Bytecode</h2>
                <p>
                    Why did we say Local variables are faster? Let's use the `dis` (disassembler) module to see what Python is actually doing.
                </p>

                <CodeBlock language="python" code={`import dis

global_var = 10

def pure_local():
    local_var = 10
    return local_var

def pure_global():
    return global_var

print("--- Local ---")
dis.dis(pure_local)

print("--- Global ---")
dis.dis(pure_global)`} />
                <p>
                    If you ran this, you would see:
                </p>
                <ul>
                    <li><strong>Local:</strong> Uses `LOAD_FAST`. This is an array lookup (O(1) and instant).</li>
                    <li><strong>Global:</strong> Uses `LOAD_GLOBAL`. This is a dictionary hash lookup (slower).</li>
                </ul>
                <ProTip><p><strong>Optimization Tip:</strong> In tight loops (like image processing), assign global functions to local pointers (e.g., `local_len = len`) to get that `LOAD_FAST` speed boost.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>8. Introspection Tools: Seeing the Invisible</h2>
                <p>
                    Python provides tools to "see" the current scope dictionaries.
                </p>

                <h3>globals() vs locals()</h3>
                <p>
                    `globals()` returns the <em>actual</em> dictionary of the module. If you change it, the variable changes.
                    `locals()` in a function, however, returns a <strong>copy</strong> of the local namespace.
                </p>
                <CodeBlock language="python" code={`x = 10

def inspect_me():
    y = 20
    # Trying to hack local variables via dictionary
    locals()['y'] = 999 
    print(y) # Output: 20 (Unchanged!)
    
    # Trying to hack global variables
    globals()['x'] = 999
    print(x) # Output: 999 (Changed!)`} />
                <ProTip><p><strong>Warning:</strong> Never modify `locals()` directly. It usually doesn't work and is largely undefined behavior.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>9. Deep Dive: The Class Body Scope Trap</h2>
                <p>
                    Here is a question that fails 90% of senior Python interviews.
                    Scope behaves normally inside methods, but <strong>inside the class body itself</strong>, it behaves strangely.
                </p>

                <CodeBlock language="python" code={`class WeirdScope:
    base = 10
    
    # 1. This works (Accessing 'base' in assignment)
    doubled = base * 2 
    
    # 2. This FAILS in Python 3 (List Comprehension)
    # The comprehension has its own scope and can't see 'base'!
    # items = [base + i for i in range(5)] 
    #  NameError: name 'base' is not defined

    # 3. But ANY normal function (def) also can't see 'base' directly?
    def show(self):
        # print(base) #  NameError
        print(self.base) # Must use 'self' or 'WeirdScope'
`} />
            </div>

            <div className="content-section">
                <h2>10. Advanced: Sandboxing with eval()</h2>
                <p>
                    The dangerous `eval()` function takes a string of code and runs it.
                    But did you know you can control its scope? You can pass custom dictionaries for `globals` and `locals` to create a "Sandbox".
                </p>
                <CodeBlock language="python" code={`code = "x + y"

# A completely empty scope
safe_globals = {"__builtins__": None}
safe_locals = {"x": 5, "y": 10}

result = eval(code, safe_globals, safe_locals)
print(result) # 15

# If the user tries to hack...
# eval("__import__('os').system('rm -rf /')", safe_globals, safe_locals)
#  TypeError: 'NoneType' object is not subscriptable (Import blocked!)`} />
            </div>

            <div className="content-section">
                <h2>11. Closures: State without Classes</h2>
                <p>
                    A <strong>Closure</strong> is a function that remembers values from its enclosing scope even after the outer function has finished executing.
                    This allows you to hide state in a function's "backpack".
                </p>

                <h3>Under the Hood: The `__closure__` Attribute</h3>
                <p>
                    How does Python remember `exponent`? It stores it in a special tuple called `__closure__`.
                    Each item in this tuple is a "Cell" object that points to the value.
                </p>

                <CodeBlock language="python" code={`def power_factory(exponent):
    def power(base):
        return base ** exponent
    return power

square = power_factory(2)

# Inspecting the backpack
print(square.__closure__) 
# (<cell at 0x...: int object at 0x...>,)

print(square.__closure__[0].cell_contents) 
# 2 (There it is!)`} />
                <p>
                    This is the functional alternative to Object-Oriented Programming (Classes).
                </p>
            </div>

            <div className="content-section">
                <h2>12. Language Face-off: Python vs JavaScript</h2>
                <p>
                    If you are coming from JavaScript, Python's scope rules can be jarring.
                </p>
                <ul>
                    <li><strong>JavaScript:</strong> `let` and `const` have <strong>Block Scope</strong>. If you define a variable inside an `if` block, it dies at the closing {"}"}.</li>
                    <li><strong>Python:</strong> Has <strong>Function Scope</strong>. Variables defined inside an `if` or `for` block LEAK out to the entire function.</li>
                </ul>
                <CodeBlock language="python" code={`# Python
if True:
    x = 10
print(x) # 10 (Alive!)`} />
                <CodeBlock language="javascript" code={`// JavaScript
if (true) {
    let x = 10;
}
console.log(x); // Error! (Dead)`} />
            </div>

            <div className="content-section">
                <h2>13. Theory: Lexical vs Dynamic Scope</h2>
                <p>
                    You might hear that Python is a <strong>Lexically Scoped</strong> (or Statically Scoped) language.
                    This means the scope consists of the physical text structure of your program.
                </p>
                <ProTip><p><strong>Thought Experiment:</strong> Imagine Python was Dynamically Scoped.</p></ProTip>
                <CodeBlock language="python" code={`x = 10

def print_x():
    print(x)

def runner():
    # In Dynamic Scope, print_x would look HERE for 'x' because runner called it.
    x = 999 
    print_x()

runner()

# Python (Lexical): Prints 10 (Looks at GLOBAL definition)
# Bash/Lisp (Dynamic): Would print 999 (Looks at CALLER definition)`} />
                <p>
                    Lexical scope is preferred in modern CS because it makes code predictable. You don't need to know <em>who</em> called your function to know what variables it sees.
                </p>
            </div>

            <div className="content-section">
                <h2>14. Pruning Scope: The `del` Keyword</h2>
                <p>
                    You can remove a name from the current scope using `del`.
                    This doesn't necessarily delete the object from memory (garbage collector handles that), but it removes the label.
                </p>
                <CodeBlock language="python" code={`x = "secret"
del x

# print(x) 
#  NameError: name 'x' is not defined`} />
                <p>
                    This is useful in Jupyter Notebooks to free up RAM if you are holding onto a massive Pandas DataFrame you no longer need.
                </p>
            </div>

            <div className="content-section">
                <h2>15. Scope Across Files: Modules and Namespaces</h2>
                <p>
                    So far, we've discussed scope inside a single file. But how does Python handle large projects?
                    Every file in Python is a <strong>Module</strong>, and every module has its own <strong>Global Scope</strong>.
                </p>

                <h3>The `__init__.py` File</h3>
                <p>
                    To turn a folder into a "Package" (a collection of modules), you traditionally put an `__init__.py` file in it.
                    Variables defined in `__init__.py` become available at the <em>package level</em> scope.
                </p>
                <CodeBlock language="python" code={`# my_package/__init__.py
version = "1.0.0"

# main.py
import my_package
import { ProTip } from '../../components/LessonUI';
import { ProTip } from '../../components/LessonUI';
print(my_package.version) # "1.0.0"`} />
            </div>

            <div className="content-section">
                <h2>16. The `__main__` Scope</h2>
                <p>
                    You have seen `if __name__ == "__main__":` a thousand times. But what does it mean for scope?
                </p>
                <p>
                    When you run a file directly (`python script.py`), its internal name is set to `"__main__"`.
                    Variables defined here are in the <strong>Global Scope</strong> of the script.
                </p>
                <ProTip><p>
                        <strong>Best Practice:</strong> Don't leave loose code in the global scope. Wrap your main logic in a `main()` function.
                        Why? Because if another file imports your script, those "loose" global variables will run via side-effects!
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>17. Advanced: Thread Safety and Globals</h2>
                <ProTip><p><strong>Critical Warning:</strong> Global variables are not just bad style; they are dangerous in multi-threaded programs.</p></ProTip>
                <p>
                    Even though Python has a Global Interpreter Lock (GIL), operations on global variables are often <strong>not atomic</strong>.
                    Consider `x += 1`. This is actually three steps: Read `x`, Add 1, Write `x`.
                    If two threads do this at the same time, they can overwrite each other, leading to "Race Conditions".
                </p>
                <CodeBlock language="python" code={`import threading

counter = 0

def increment():
    global counter
    for _ in range(100000):
        # This looks safe, but it isn't atomically safe!
        counter += 1 

# If you run this with 10 threads, 'counter' will mostly likely NOT be 1,000,000.`} />
                <p>
                    <strong>Solution:</strong> Always use `threading.Lock()` when modifying globals, or better yet, don't use globals at all.
                </p>
            </div>

            <div className="content-section">
                <h2>18. The "Star Import" Pollution</h2>
                <p>
                    You often see `from math import *`. This is called a "Wildcard Import".
                    It is dangerous because it dumps <em>every variable</em> from that module into your current Global Scope.
                </p>
                <CodeBlock language="python" code={`from math import *
# ... 500 lines of code ...
tan = "My suntan level" 
# Oops! You just overwrote the tangent function from math!

# Later...
print(tan(45)) 
#  TypeError: 'str' object is not callable`} />
            </div>

            <div className="content-section">
                <h2>19. Scope vs Memory: Garbage Collection</h2>
                <p>
                    Scope isn't just about visibility; it's about life and death.
                    When a variable goes "out of scope" (e.g., a function returns), Python decreases its <strong>Reference Count</strong>.
                </p>
                <p>
                    When the Reference Count hits zero, the Garbage Collector (GC) deletes the object to free RAM.
                </p>
                <CodeBlock language="python" code={`class Life:
    def __del__(self):
        print("I am dying!")

def short_life():
    x = Life()
    print("Alive inside function")
    # Function ends here. Scope dies. 'x' dies.

short_life()
# Output:
# Alive inside function
# I am dying!`} />
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><ul>
                        <li><strong>Minimize Globals:</strong> Global state makes code hard to test and debug. Pass arguments instead.</li>
                        <li><strong>Use `nonlocal` sparingly:</strong> If you need deep state, write a Class instead of a Closure.</li>
                        <li><strong>Avoid Shadowing:</strong> Never name variables `list`, `str`, or `id`.</li>
                        <li><strong>Trust the Function:</strong> Functions should be self-contained units.</li>
                    </ul></ProTip>
            </div>

        </div>
    );
};

export default PythonScope;


